import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrimeTemplate } from 'primeng/api';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';

import { GRID_PAGINATOR_CONFIG, GridPaginatorPageLinkSizes, GridPaginatorRows } from '@ppg/core/constants';
import { ResourceItem } from '@ppg/core/enums';
import { Color, FavoriteColor } from '@ppg/core/models';
import { ColorChipComponent } from '@ppg/shared/color/color-chip';
import { FavoriteColorLocalStorageService, FavoriteColorsService } from '@ppg/shared/color/services';
import { HorizontalDividerComponent, ScrollOnPageChangeDirective } from '@ppg/shared/components';
import { ResourceValuePipe } from '@ppg/shared/resource';

@Component({
  selector: 'ppg-colors-grid',
  standalone: true,
  imports: [
    ColorChipComponent,
    HorizontalDividerComponent,
    PaginatorModule,
    ResourceValuePipe,
    CommonModule,
    ScrollOnPageChangeDirective,
  ],
  templateUrl: './colors-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorsGridComponent {
  readonly #favoriteColorStorageService = inject(FavoriteColorLocalStorageService);
  readonly #favoriteColorsService = inject(FavoriteColorsService);
  readonly #destroyRef = inject(DestroyRef);

  readonly paginatorConfig = GRID_PAGINATOR_CONFIG;

  /**
   * The flag that indicates the color-grid to handle pagination automatically.
   * In this case you should pass all colors at once.
   * The pagination events won't be emitted.
   */
  readonly automaticPagination = input<boolean>(true);
  /**
   * Data count to display per page.
   */
  readonly rows = input.required<GridPaginatorRows>();
  /**
   * Number of total records. Should be provided when automaticPagination is false.
   */
  readonly total = input<number>();
  /**
   * Zero-relative number of the first row to be displayed. Should be provided when automaticPagination is false.
   */
  readonly first = input<number>(this.paginatorConfig.first);

  /**
   * Number of page links to display.
   */
  readonly pageLinkSize = input<number>(GridPaginatorPageLinkSizes.default);

  /**
   * Callback to invoke when page changes, the event object contains information about the new state. Emmited only when the automaticPagination is false.
   */
  readonly onPageChange = output<PaginatorState>();

  readonly title = input<string>();
  readonly gridItemStyleClass = input<string>();

  readonly colors = input.required<Color[] | null>();

  readonly totalRecords = computed(() =>
    this.automaticPagination() ? (this.colors()?.length ?? 0) : (this.total() ?? 0),
  );

  readonly colorsPage = computed(() => {
    const colors = this.colors();
    const favoriteColors = this.#favoriteColorStorageService.favoriteColors();

    return this.setUpColorsPage(colors, favoriteColors);
  });

  readonly numberOfItemsDisplayedPerPage = computed(() =>
    this.automaticPagination() && this.automaticallyCalculatedRowsPerPage()
      ? this.automaticallyCalculatedRowsPerPage()
      : this.rows(),
  );
  readonly automaticallyCalculatedRowsPerPage = signal(0);
  readonly automaticallyCalculatedFirstRow = signal(this.paginatorConfig.first);
  readonly firstRow = computed(() =>
    this.automaticPagination() ? this.automaticallyCalculatedFirstRow() : this.first(),
  );

  readonly componentStyleClass = computed(() => this.gridItemStyleClass() ?? this.defaultStyleClass);
  readonly defaultStyleClass = 'flex md:block p-2 w-full md:w-6 lg:w-3 xl:w-3 md:col-6 lg:col-3 xl:col-3';

  readonly favoriteColorAdded = output<Color>();
  readonly favoriteColorRemoved = output<Color>();
  readonly colorDetailsLinkClicked = output<Color>();

  @ContentChild(PrimeTemplate, { read: TemplateRef }) emptyMessageTemplate!: TemplateRef<unknown> | undefined;

  readonly resources = {
    toastSummary: ResourceItem.TOAST_REMOVE_FAVORITE_SUCCESS_CONFIRMATION_TITLE,
    toastDetailRemove: ResourceItem.TOAST_REMOVE_FAVORITE_SUCCESS_CONFIRMATION_MESSAGE,
    toastDetailAdd: ResourceItem.TOAST_ADD_FAVORITE_SUCCESS_CONFIRMATION_MESSAGE,
    currentPageReportTemplateResourceKey: ResourceItem.COLORS_GRID_PAGINATION,
    confirmationDialogHeaderText: ResourceItem.CONFIRMATION_DIALOG_REMOVE_FAVORITE_TITLE,
    confirmationDialogBodyText: ResourceItem.CONFIRMATION_DIALOG_REMOVE_FAVORITE_MESSAGE,
    cancelButton: ResourceItem.CONFIRMATION_DIALOG_REMOVE_FAVORITE_CANCEL_BUTTON,
    submitButton: ResourceItem.CONFIRMATION_DIALOG_REMOVE_FAVORITE_CONFIRM_BUTTON,
  };

  constructor() {
    effect(
      () => {
        if (this.colors() && this.automaticPagination()) {
          this.resetPaginatorFirstRow();
        }
      },
      { allowSignalWrites: true },
    );

    this.#favoriteColorsService.favoriteColorAdded$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((color) => this.favoriteColorAdded.emit(color));

    this.#favoriteColorsService.favoriteColorRemoved$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((color) => this.favoriteColorRemoved.emit(color));
  }

  onFavoriteButtonClicked(color: Color): void {
    if (color.isFavorite) {
      this.#favoriteColorsService.removeFromFavorites(color);
    } else {
      this.#favoriteColorsService.addToFavorites(color);
    }
  }

  onColorDetailsLinkClicked(color: Color): void {
    this.colorDetailsLinkClicked.emit(color);
  }

  onPageUpdate(event: PaginatorState): void {
    if (!this.automaticPagination()) {
      this.onPageChange.emit(event);
      return;
    }

    if (event.first !== undefined && event.rows !== undefined) {
      this.automaticallyCalculatedFirstRow.set(event.first);
      this.automaticallyCalculatedRowsPerPage.set(event.rows);
    }
  }

  private setUpColorsPage(colors: Color[] | null, favoriteColors: FavoriteColor[]): Color[] {
    let colorsPerPage = this.automaticPagination() ? this.getColorsPage(colors) : colors;

    if (colorsPerPage && favoriteColors) {
      colorsPerPage = this.setFavoriteColors(colorsPerPage, favoriteColors);
    }

    return colorsPerPage ?? [];
  }

  private setFavoriteColors(colors: Color[], favoriteColors: FavoriteColor[]): Color[] {
    return [...colors].map((color) => {
      const favoriteColor = favoriteColors.find((c) => c.productNumber === color.productNumber);

      return {
        ...color,
        isFavorite: Boolean(favoriteColor),
        addedAsFavoriteAt: favoriteColor ? new Date(favoriteColor.addedToFavoritesAt) : undefined,
      };
    });
  }

  private getColorsPage(colors: Color[] | null): Color[] {
    const take = this.automaticallyCalculatedRowsPerPage() ? this.automaticallyCalculatedRowsPerPage() : this.rows();
    return colors?.slice(this.automaticallyCalculatedFirstRow(), this.automaticallyCalculatedFirstRow() + take) ?? [];
  }

  private resetPaginatorFirstRow(): void {
    this.automaticallyCalculatedFirstRow.set(this.paginatorConfig.first);
  }
}
